import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../Context/auth";

const Login = () => {
  const navigate = useNavigate();
  const [auth, setAuth] = useAuth();
  const [formData, setFormData] = useState({
    identifier: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.identifier || !formData.password) {
      toast.error("Both fields are required");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        "https://bioopay-backendfull-code-production.up.railway.app/api/v1/users/login",
        {
          identifier: formData.identifier,
          password: formData.password,
        }
      );

      if (response.data.success) {
        toast.success("Login successful!");
        const { user, token } = response.data;

        // Save token and user data in localStorage
        localStorage.setItem("auth", JSON.stringify({ user, token }));

        // Optionally, update global auth context if you have it
        setAuth({ ...auth, user, token });

        // Redirect to dashboard based on user role
        navigate(`/dashboard/${user.role === "admin" ? "admin" : "user"}`);
      }
    } catch (error) {
      const message = error.response?.data?.message || "Login failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.mainContainer}>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        toastStyle={{
          backgroundColor: "#1E293B",
          color: "#F8FAFC",
          borderRadius: "12px",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
        }}
      />

      <div style={styles.loginContainer}>
        {/* Header with Logo */}
        <div style={styles.header}>
          <div style={styles.logo}>
            <span style={styles.logoAccent}>Bloo</span>pay
          </div>
          <h2 style={styles.title}>Welcome Back</h2>
          <p style={styles.subtitle}>Sign in to continue to your account</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Email/Username Field */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>Email or Username</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <path d="M22 6l-10 7L2 6"></path>
                </svg>
              </span>
              <input
                name="identifier"
                type="text"
                required
                value={formData.identifier}
                onChange={handleChange}
                style={styles.input}
                placeholder="your@email.com or username"
              />
            </div>
          </div>

          {/* Password Field */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>Password</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
              </span>
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                required
                value={formData.password}
                onChange={handleChange}
                style={styles.input}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.passwordToggle}
              >
                {showPassword ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"></path>
                    <path d="M1 1l22 22"></path>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Forgot Password Link */}
          <div style={styles.forgotPassword}>
            <a href="/forgot-password" style={styles.forgotPasswordLink}>
              Forgot password?
            </a>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.submitButton,
              ...(loading && styles.submitButtonDisabled),
            }}
          >
            {loading ? (
              <>
                <svg
                  style={styles.spinner}
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
                Signing in...
              </>
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        {/* Divider */}
        <div style={styles.divider}>
          <div style={styles.dividerLine}></div>
          <div style={styles.dividerText}>OR</div>
          <div style={styles.dividerLine}></div>
        </div>

        {/* Register Link */}
        <div style={styles.registerLink}>
          Don't have an account?{" "}
          <a href="/register" style={styles.registerLinkText}>
            Create one
          </a>
        </div>
      </div>

      {/* Add animation keyframes */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% {transform: translateY(0);}
            40% {transform: translateY(-10px);}
            60% {transform: translateY(-5px);}
          }
        `}
      </style>
    </div>
  );
};

const styles = {
  mainContainer: {
    fontFamily: "'Inter', sans-serif",
    backgroundColor: "#0a0a0a",
    color: "#e0e0e0",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    lineHeight: 1.6,
  },
  loginContainer: {
    width: "100%",
    maxWidth: "480px",
    padding: "30px",
    backgroundColor: "#1a1a1a",
    borderRadius: "15px",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
    border: "1px solid rgba(255, 255, 255, 0.05)",
    position: "relative",
    overflow: "hidden",
    margin: "20px 0",
    "@media (max-width: 480px)": {
      padding: "25px 20px",
      borderRadius: "12px",
    },
    "::before": {
      content: '""',
      position: "absolute",
      top: "-100px",
      right: "-100px",
      width: "300px",
      height: "300px",
      background:
        "radial-gradient(circle, rgba(138,43,226,0.15) 0%, rgba(0,0,0,0) 70%)",
      zIndex: 0,
    },
  },
  header: {
    textAlign: "center",
    marginBottom: "2rem",
    position: "relative",
    zIndex: 1,
    "@media (max-width: 480px)": {
      marginBottom: "1.5rem",
    },
  },
  logo: {
    fontSize: "2.2rem",
    fontWeight: 700,
    marginBottom: "1.5rem",
    background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "@media (max-width: 480px)": {
      fontSize: "1.8rem",
      marginBottom: "1rem",
    },
  },
  logoAccent: {
    fontWeight: 800,
    marginRight: "0.3rem",
  },
  title: {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: "0.5rem",
    "@media (max-width: 480px)": {
      fontSize: "1.5rem",
    },
  },
  subtitle: {
    fontSize: "0.95rem",
    color: "#b0b0b0",
    fontWeight: 400,
    "@media (max-width: 480px)": {
      fontSize: "0.85rem",
    },
  },
  form: {
    position: "relative",
    zIndex: 1,
  },
  inputGroup: {
    marginBottom: "1.2rem",
    "@media (max-width: 480px)": {
      marginBottom: "1rem",
    },
  },
  label: {
    display: "block",
    fontSize: "0.95rem",
    fontWeight: 500,
    color: "#b0b0b0",
    marginBottom: "0.6rem",
    "@media (max-width: 480px)": {
      fontSize: "0.9rem",
      marginBottom: "0.5rem",
    },
  },
  inputWrapper: {
    position: "relative",
  },
  input: {
    width: "100%",
    padding: "14px 14px 14px 44px",
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "8px",
    color: "#ffffff",
    fontSize: "0.95rem",
    outline: "none",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    "::placeholder": {
      color: "#555555",
      fontSize: "0.9rem",
    },
    ":focus": {
      borderColor: "#8a2be2",
      boxShadow: "0 0 0 3px rgba(138, 43, 226, 0.2)",
    },
    "@media (max-width: 480px)": {
      padding: "12px 12px 12px 40px",
      fontSize: "0.9rem",
    },
  },
  inputIcon: {
    position: "absolute",
    left: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#b0b0b0",
    fontSize: "1.2rem",
    "@media (max-width: 480px)": {
      left: "12px",
    },
  },
  passwordToggle: {
    position: "absolute",
    right: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#b0b0b0",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: "none",
    border: "none",
    padding: "0",
    ":hover": {
      color: "#ffffff",
    },
    "@media (max-width: 480px)": {
      right: "12px",
    },
  },
  forgotPassword: {
    textAlign: "right",
    marginTop: "-0.5rem",
    marginBottom: "1.2rem",
    "@media (max-width: 480px)": {
      marginBottom: "1rem",
    },
  },
  forgotPasswordLink: {
    fontSize: "0.85rem",
    color: "#b0b0b0",
    textDecoration: "none",
    transition: "all 0.2s ease",
    fontWeight: 500,
    ":hover": {
      color: "#ffffff",
    },
  },
  submitButton: {
    width: "100%",
    padding: "14px",
    background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
    color: "white",
    fontWeight: 600,
    borderRadius: "8px",
    border: "none",
    fontSize: "1rem",
    cursor: "pointer",
    boxShadow: "0 5px 20px rgba(138, 43, 226, 0.3)",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "0.5rem",
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(138, 43, 226, 0.5)",
    },
    "@media (max-width: 480px)": {
      padding: "12px",
      fontSize: "0.95rem",
    },
  },
  submitButtonDisabled: {
    opacity: "0.7",
    cursor: "not-allowed",
    transform: "none !important",
  },
  spinner: {
    animation: "spin 1s linear infinite",
    marginRight: "0.5rem",
  },
  divider: {
    display: "flex",
    alignItems: "center",
    margin: "1.5rem 0",
    position: "relative",
    zIndex: 1,
    "@media (max-width: 480px)": {
      margin: "1.2rem 0",
    },
  },
  dividerLine: {
    flex: "1",
    height: "1px",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
  dividerText: {
    padding: "0 1rem",
    color: "#b0b0b0",
    fontSize: "0.85rem",
    fontWeight: 500,
  },
  registerLink: {
    textAlign: "center",
    color: "#b0b0b0",
    fontSize: "0.9rem",
    position: "relative",
    zIndex: 1,
    "@media (max-width: 480px)": {
      fontSize: "0.85rem",
    },
  },
  registerLinkText: {
    color: "#8a2be2",
    textDecoration: "none",
    fontWeight: 500,
    transition: "all 0.2s ease",
    ":hover": {
      color: "#00bfff",
    },
  },
};

export default Login;
